const monthNumbers = {jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12};

function wallTimeToEpoch(parts, timeZone) {
  if (timeZone === 'America/Vancouver' && Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute) >= Date.UTC(2026, 2, 8, 3)) {
    const epoch = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute) + 7 * 3600000;
    const local = new Date(epoch - 7 * 3600000);
    return local.getUTCFullYear() === parts.year && local.getUTCMonth() === parts.month - 1 && local.getUTCDate() === parts.day ? epoch : null;
  }
  const wall = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  const check = new Date(wall);
  if (check.getUTCFullYear() !== parts.year || check.getUTCMonth() !== parts.month - 1 || check.getUTCDate() !== parts.day) return null;
  const formatter = new Intl.DateTimeFormat('en-US', {timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'});
  const read = epoch => Object.fromEntries(formatter.formatToParts(new Date(epoch)).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  let epoch = wall;
  for (let pass = 0; pass < 4; pass += 1) {
    const local = read(epoch);
    const offset = wall - Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    if (offset === 0) return epoch;
    epoch += offset;
  }
  const local = read(epoch);
  return ['year', 'month', 'day', 'hour', 'minute'].every(key => local[key] === parts[key]) ? epoch : null;
}

export function parseClassStart(start, title, timeZone = 'America/Vancouver') {
  if (start && start.trim()) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.test(start.trim())) return null;
    const epoch = Date.parse(start);
    return Number.isFinite(epoch) ? epoch : null;
  }
  const match = String(title).match(/\b(January|Jan|February|Feb|March|Mar|April|Apr|May|June|Jun|July|Jul|August|Aug|September|Sept|Sep|October|Oct|November|Nov|December|Dec)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:\/|,?\s+)(\d{4}|\d{2})\s+at\s+(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?\b/i);
  if (!match) return null;
  const hour = Number(match[4]);
  const minute = Number(match[5] || 0);
  if (hour < 1 || hour > 12 || minute > 59) return null;
  return wallTimeToEpoch({year: match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]), month: monthNumbers[match[1].slice(0, 3).toLowerCase()], day: Number(match[2]), hour: hour % 12 + (match[6].toLowerCase() === 'p' ? 12 : 0), minute}, timeZone);
}

export function selectUpcoming(records, now, limit = 12) {
  return records.filter(record => Number.isFinite(record.start) && record.start > now && !record.cancelled).sort((a, b) => a.start - b.start).slice(0, limit);
}

if (typeof window !== 'undefined' && !customElements.get('yeschef-upcoming-classes')) {
  customElements.define('yeschef-upcoming-classes', class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.initialized = true;
      this.timeZone = this.dataset.timezone || 'America/Vancouver';
      this.locale = this.dataset.locale || 'en';
      this.limit = Number(this.dataset.limit) || 12;
      try {
        await this.loadPages();
        this.records = Array.from(this.querySelectorAll('[data-class-card]')).map(card => ({card, start: parseClassStart(card.dataset.start, card.dataset.classTitle, this.timeZone)}));
        this.records.forEach(record => {
          if (!Number.isFinite(record.start)) return;
          const date = new Date(record.start);
          const format = options => window.YCClassTime.format(date, this.locale, options, this.timeZone);
          const badge = record.card.querySelector('[data-date-badge]');
          badge.dateTime = date.toISOString();
          badge.setAttribute('aria-label', format({dateStyle: 'full', timeStyle: 'short'}));
          record.card.querySelector('[data-month]').textContent = format({month: 'long', year: 'numeric'});
          record.card.querySelector('[data-day]').textContent = format({day: 'numeric'});
          record.card.querySelector('[data-weekday]').textContent = format({weekday: 'long'});
          const time = record.card.querySelector('[data-time]');
          time.dateTime = date.toISOString();
          time.textContent = format({hour: 'numeric', minute: '2-digit'});
          record.card.querySelector('[data-title]').textContent = record.card.dataset.classTitle.replace(/\s+[-–—]\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[\s\S]*$/i, '').trim();
        });
        this.previous = this.querySelector('[data-previous]');
        this.next = this.querySelector('[data-next]');
        this.previous.addEventListener('click', () => this.move(-1));
        this.next.addEventListener('click', () => this.move(1));
        this.track.addEventListener('scroll', () => this.updateControls(), {passive: true});
        this.onVisibility = () => { if (!document.hidden) this.refresh(); };
        document.addEventListener('visibilitychange', this.onVisibility);
        this.observer = new ResizeObserver(() => this.updateControls());
        this.observer.observe(this.track);
        this.refresh();
      } catch (error) {
        this.querySelector('[data-load-error]').hidden = false;
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.initialized = false;
    }
    async loadPages() {
      let next = this.querySelector('[data-next-page]')?.getAttribute('href');
      const seen = new Set();
      const ids = new Set(Array.from(this.track.querySelectorAll('[data-class-card]')).map(card => card.dataset.productId));
      while (next) {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid class pagination');
        seen.add(url.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('Unable to load classes');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.querySelector('yeschef-upcoming-classes');
        if (!section) throw new Error('Class section not found');
        section.querySelectorAll('[data-class-card]').forEach(card => {
          if (!ids.has(card.dataset.productId)) {
            ids.add(card.dataset.productId);
            this.track.append(card);
          }
        });
        next = section.querySelector('[data-next-page]')?.getAttribute('href');
      }
    }
    refresh() {
      clearTimeout(this.timer);
      const now = Date.now();
      const events = selectUpcoming(this.records, now, this.limit);
      const cards = events.map(event => event.card);
      if (cards.length !== this.track.children.length || cards.some((card, index) => this.track.children[index] !== card)) this.track.replaceChildren(...cards);
      cards.forEach(card => {
        card.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
        card.hidden = false;
      });
      this.querySelector('[data-viewport]').hidden = cards.length === 0;
      this.querySelector('[data-empty]').hidden = cards.length !== 0;
      this.querySelector('[data-controls]').hidden = cards.length < 2;
      requestAnimationFrame(() => this.updateControls());
      this.timer = setTimeout(() => this.refresh(), Math.max(1000, Math.min(60000, (events[0]?.start - now) || 60000)));
    }
    updateControls() {
      if (!this.previous || !this.next) return;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
      this.querySelector('[data-controls]').hidden = max <= 2;
    }
    move(direction) {
      const card = this.track.querySelector('[data-class-card]');
      const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0) : this.track.clientWidth;
      this.track.scrollBy({left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
  });
}
